import type { CardSummary, HistoryMessage, YouTubeVideoItem } from '../src/shared/contracts';
import { renderMarkdown } from './markdown';

function createIconSvg(pathD: string, className = 'w-6 h-6'): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', className);

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute('stroke-width', '2');
  path.setAttribute('d', pathD);
  svg.append(path);

  return svg;
}

export function clearElement(element: Element): void {
  while (element.firstChild) {
    element.firstChild.remove();
  }
}

export function createMessageBubble(
  message: HistoryMessage | { kind: 'text'; sender: 'assistant' | 'user'; text: string },
): HTMLDivElement {
  const bubble = document.createElement('div');
  bubble.className = message.sender === 'user' ? 'user-message message-text' : 'assistant-message message-text';

  if (message.kind === 'text') {
    if (message.sender === 'assistant') {
      const content = document.createElement('div');
      content.className = 'markdown-content';
      content.append(renderMarkdown(message.text));
      bubble.append(content);
    } else {
      bubble.textContent = message.text;
    }
  }

  return bubble;
}

export function createLoadingIndicator(): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'loading-indicator';

  const dots = document.createElement('div');
  dots.className = 'loading-dots';
  for (let index = 0; index < 3; index += 1) {
    dots.append(document.createElement('span'));
  }

  wrapper.append(dots);
  return wrapper;
}

export function createErrorBubble(message: string): HTMLDivElement {
  const bubble = document.createElement('div');
  bubble.className = 'assistant-message error-message message-text';
  bubble.textContent = message;
  return bubble;
}

export function createStatusMessage(text: string): HTMLDivElement {
  const message = document.createElement('div');
  message.className = 'status-message';
  message.textContent = text;
  return message;
}

export function createVideoResults(
  videos: YouTubeVideoItem[],
  onSelect: (videoId: string) => void,
): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'video-results-container assistant-message';

  if (videos.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'message-text';
    empty.textContent = 'No videos found for your query.';
    container.append(empty);
    return container;
  }

  for (const video of videos) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'video-item text-left';
    item.addEventListener('click', () => onSelect(video.videoId));

    const image = document.createElement('img');
    image.className = 'video-thumbnail';
    image.src = video.thumbnailUrl;
    image.alt = video.title;

    const info = document.createElement('div');
    info.className = 'video-info';

    const title = document.createElement('h4');
    title.className = 'video-title';
    title.textContent = video.title;

    const channel = document.createElement('p');
    channel.className = 'video-channel-title';
    channel.textContent = video.channelTitle;

    info.append(title, channel);
    item.append(image, info);
    container.append(item);
  }

  return container;
}

export function createCardTile(card: CardSummary, tileClass: string): HTMLButtonElement {
  const tile = document.createElement('button');
  tile.type = 'button';
  tile.className = `card-tile ${tileClass} text-white p-6 rounded-lg shadow-md aspect-square flex flex-col justify-center items-center cursor-pointer`;
  tile.dataset.cardId = card.id;

  const title = document.createElement('h2');
  title.className = 'text-xl sm:text-2xl font-bold text-center leading-tight mb-2';
  title.textContent = card.name;

  const icon = document.createElement('div');
  icon.className = 'mt-2 text-2xl flex items-center justify-center';
  if (card.isAudio) {
    icon.append(
      createIconSvg(
        'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z',
        'w-7 h-7',
      ),
    );
  }

  tile.append(title, icon);
  return tile;
}
